import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    IconButton,
    Button,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Avatar,
    Typography,
    Divider,
    Box,
    CircularProgress,
    Switch
} from '@mui/material';
import {
    Menu,
    Person,
    Settings,
    Logout,
    Login,
    DarkMode,
    Close
} from '@mui/icons-material';
import { useAuthentication, useTheme } from 'hooks';

export function TopNavbar() {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const {
        isAuthenticated,
        user,
        userFullName,
        userPhotoUrl,
        loading,
        signOut
    } = useAuthentication();

    const { themeMode, toggleTheme } = useTheme();
    const navigate = useNavigate();

    const toggleDrawer = (open) => () => {
        setDrawerOpen(open);
    };

    const handleSignOut = async () => {
        setDrawerOpen(false);
        await signOut();
        navigate('/', { replace: true });
    };

    const handleSignIn = () => {
        setDrawerOpen(false);
        navigate('/sign-in');
    };

    const handleNavigation = (path) => () => {
        setDrawerOpen(false);
        navigate(path);
    };

    const handleThemeToggle = (event) => {
        event.preventDefault();
        event.stopPropagation();
        toggleTheme();
    };

    const authenticatedItems = [
        {
            label: 'Profile',
            to: '/profile',
            icon: <Person />,
            onClick: handleNavigation('/profile')
        },
        {
            label: 'Settings',
            to: '/settings',
            icon: <Settings />,
            onClick: handleNavigation('/settings')
        },
        {
            label: 'Dark Mode',
            icon: <DarkMode />,
            isSwitch: true,
            checked: themeMode === 'dark',
            onClick: handleThemeToggle
        },
        {
            label: 'Sign out',
            icon: <Logout />,
            onClick: handleSignOut,
            divider: true
        },
    ];

    const guestItems = [
        {
            label: 'Sign in',
            icon: <Login />,
            onClick: handleSignIn
        },
    ];

    const drawerItems = isAuthenticated ? authenticatedItems : guestItems;

    const getInitials = (name) => {
        if (!name) return user?.email?.charAt(0)?.toUpperCase() || 'U';
        return name.split(' ').map(n => n.charAt(0)).join('').toUpperCase();
    };

    return (
        <>
            <AppBar position="static">
                <Toolbar>
                    <Button
                        component={Link}
                        onClick={() => window.location.href = process.env.REACT_APP_MAIN_URL}
                        color="inherit"
                        sx={{
                            typography: 'h6',
                            textTransform: 'none',
                            px: 0,
                            position: { xs: 'static', md: 'absolute' },
                            left: { md: '50%' },
                            transform: { md: 'translateX(-50%)' },
                            zIndex: 1,
                        }}
                        aria-label="Go to home"
                    >
                        {process.env.REACT_APP_NAME}
                    </Button>

                    <Box sx={{ flexGrow: 1 }} />

                    {loading && (
                        <CircularProgress size={24} color="inherit" sx={{ mr: 2 }} />
                    )}

                    {isAuthenticated ? (
                        <Avatar
                            src={userPhotoUrl || undefined}
                            sx={{
                                width: 36,
                                height: 36,
                                cursor: 'pointer',
                                fontSize: '1rem'
                            }}
                            onClick={toggleDrawer(true)}
                            alt={userFullName || user?.email}
                        >
                            {!userPhotoUrl && getInitials(userFullName)}
                        </Avatar>
                    ) : (
                        <IconButton
                            edge="end"
                            color="inherit"
                            aria-label="menu"
                            onClick={toggleDrawer(true)}
                            disabled={loading}
                        >
                            <Menu />
                        </IconButton>
                    )}
                </Toolbar>
            </AppBar>

            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={toggleDrawer(false)}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: { xs: '100vw', sm: '100vw', md: 300, lg: 300, xl: 300 },
                        maxWidth: '100vw'
                    }
                }}
            >
                {isAuthenticated && user && (
                    <>
                        <Box sx={{
                            p: { xs: 3, md: 2 },
                        }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 1
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Avatar
                                        src={userPhotoUrl || undefined}
                                        sx={{
                                            width: { xs: 56, md: 48 },
                                            height: { xs: 56, md: 48 },
                                            mr: 2
                                        }}
                                        alt={userFullName || user.email}
                                    >
                                        {!userPhotoUrl && getInitials(userFullName)}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: { xs: '1.1rem', md: '1rem' } }}>
                                            {userFullName || user?.firstName || 'User'}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                opacity: 0.8,
                                                fontSize: { xs: '0.9rem', md: '0.875rem' }
                                            }}
                                        >
                                            {user.email}
                                        </Typography>
                                    </Box>
                                </Box>

                                <IconButton
                                    onClick={toggleDrawer(false)}
                                    aria-label="Close menu"
                                    size="small"
                                    sx={{ display: { xs: 'flex', md: 'none' } }}
                                >
                                    <Close />
                                </IconButton>
                            </Box>
                        </Box>
                    </>
                )}

                <List>
                    {!isAuthenticated && (
                        <ListItem disablePadding>
                            <ListItemButton
                                onClick={handleSignIn}
                                disabled={loading}
                                sx={{
                                    py: { xs: 2, md: 1.5 },
                                    px: { xs: 3, md: 2 }
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: { xs: 48, md: 40 } }}>
                                    <Login />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Sign in"
                                    slotProps={{
                                        primary: {
                                            fontSize: { xs: '1rem', md: '0.875rem' }
                                        }
                                    }}
                                />
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleDrawer(false)();
                                    }}
                                    size="small"
                                    aria-label="Close menu"
                                    sx={{
                                        display: { xs: 'flex', md: 'none' },
                                        ml: 1
                                    }}
                                >
                                    <Close />
                                </IconButton>
                            </ListItemButton>
                        </ListItem>
                    )}

                    {isAuthenticated && drawerItems.map(({ label, to, onClick, icon, divider, isSwitch, checked }) => (
                        <div key={label}>
                            {divider && <Divider sx={{ my: 1 }} />}
                            <ListItem disablePadding>
                                <ListItemButton
                                    component={to ? Link : 'button'}
                                    to={to}
                                    onClick={(e) => {
                                        if (isSwitch || (onClick && !to)) {
                                            e.preventDefault();
                                        }
                                        onClick && onClick(e);
                                    }}
                                    disabled={loading}
                                    sx={{
                                        py: { xs: 2, md: 1.5 },
                                        px: { xs: 3, md: 2 }
                                    }}
                                >
                                    {icon && (
                                        <ListItemIcon sx={{ minWidth: { xs: 48, md: 40 } }}>
                                            {icon}
                                        </ListItemIcon>
                                    )}
                                    <ListItemText
                                        primary={label}
                                        slotProps={{
                                            primary: {
                                                fontSize: { xs: '1rem', md: '0.875rem' }
                                            }
                                        }}
                                    />
                                    {isSwitch && (
                                        <Switch
                                            edge="end"
                                            checked={checked}
                                            onClick={(e) => e.preventDefault()}
                                        />
                                    )}
                                </ListItemButton>
                            </ListItem>
                        </div>
                    ))}
                </List>
            </Drawer>
        </>
    );
}
